import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import { useCampaigns } from '@/features/campaigns/api/campaign-queries'
import type { Campaign, CampaignInstrument } from '@/features/campaigns/types'
import { useSessionStore } from '@/stores/useSessionStore'

import {
  flattenAllocatedInstruments,
  deriveEditorViewerDashboardMetrics,
  type AllocatedInstrumentRow,
} from '../lib/allocated-instruments-display'

export interface EditorViewerDashboardMetrics {
  totalAllocatedCampaigns: number
  totalActiveInstruments: number
}

export interface AllocatedInstrumentsDashboardData {
  campaigns: Campaign[]
  rows: AllocatedInstrumentRow[]
  metrics: EditorViewerDashboardMetrics
}

async function fetchCampaignInstruments(campaignId: string): Promise<CampaignInstrument[]> {
  const { data: envelope } = await apiClient.get<ApiResponse<CampaignInstrument[]>>(
    `/api/v1/campaigns/${campaignId}/instruments`,
  )
  return envelope.data
}

function tenantKey(tenantId: string | null | undefined): string {
  return tenantId ?? '_'
}

export function useAllocatedInstrumentsDashboard(options?: {
  refetchIntervalMs?: number
}): {
  data: AllocatedInstrumentsDashboardData | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => Promise<unknown>
} {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const refetchIntervalMs = options?.refetchIntervalMs
  const refetchInterval: number | false =
    refetchIntervalMs && refetchIntervalMs > 0 ? refetchIntervalMs : false

  const campaignsQuery = useCampaigns({
    refetchIntervalMs: refetchInterval || undefined,
  })
  const campaigns = campaignsQuery.data?.items ?? []
  const campaignIds = useMemo(
    () => campaigns.map((c) => c.id).filter((id) => id.length > 0),
    [campaigns],
  )

  const instrumentQueries = useQueries({
    queries: campaignIds.map((campaignId) => ({
      queryKey: queryKeys.campaigns.instruments(tenantKey(tenantId), campaignId),
      queryFn: () => fetchCampaignInstruments(campaignId),
      enabled: Boolean(campaignId) && campaignsQuery.isSuccess,
      staleTime: 30_000,
      refetchInterval,
    })),
  })

  const instrumentsByCampaignId = useMemo(() => {
    const map = new Map<string, CampaignInstrument[]>()
    campaignIds.forEach((campaignId, index) => {
      const instruments = instrumentQueries[index]?.data
      if (instruments) {
        map.set(campaignId, instruments)
      }
    })
    return map
  }, [campaignIds, instrumentQueries])

  const rows = useMemo(
    () => flattenAllocatedInstruments(campaigns, instrumentsByCampaignId),
    [campaigns, instrumentsByCampaignId],
  )

  const metrics = useMemo(
    () => deriveEditorViewerDashboardMetrics(campaigns, rows),
    [campaigns, rows],
  )

  const allInstrumentsFetched =
    campaignIds.length === 0 ||
    (instrumentQueries.length === campaignIds.length &&
      instrumentQueries.every((q) => q.isFetched))

  const isLoading =
    campaignsQuery.isLoading || (campaignIds.length > 0 && !allInstrumentsFetched)
  const isError = campaignsQuery.isError || instrumentQueries.some((q) => q.isError)
  const error =
    campaignsQuery.error ??
    instrumentQueries.find((q) => q.error)?.error ??
    null

  const refetch = async () => {
    await campaignsQuery.refetch()
    await Promise.all(instrumentQueries.map((q) => q.refetch()))
  }

  const data: AllocatedInstrumentsDashboardData | undefined = campaignsQuery.isSuccess
    ? { campaigns, rows, metrics }
    : undefined

  return { data, isLoading, isError, error, refetch }
}
